import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { ModulePermission } from '@core/models';
import { PermissionService } from '@core/services';

/**
 * Module Guard Factory
 * Creates a guard that checks if the user can access a module — ANY of the
 * given ones, when a screen belongs to several roles (Dispensaciones: who
 * dispenses, supervises or audits). Redirects to /access-denied otherwise.
 *
 * @example
 * const routes: Routes = [
 *   {
 *     path: 'admin',
 *     component: AdminComponent,
 *     canActivate: [moduleGuard('view_supervisor_module')]
 *   }
 * ];
 */
export function moduleGuard(...modules: ModulePermission[]): CanActivateFn {
  return async () => {
    const permissionService = inject(PermissionService);
    const router = inject(Router);

    // Wait for the user to be loaded — really wait: `user` is a signal, and
    // awaiting a signal read is awaiting whatever is already there.
    await permissionService.ready();

    const canAccess = modules.some((m) => permissionService.canAccessModule(m));
    if (canAccess) {
      return true;
    }

    // Redirect to access denied page
    return router.createUrlTree(['/access-denied']);
  };
}
